import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import en from './translations/en.json';
import pt from './translations/pt.json';
import es from './translations/es.json';

const translations = { en, pt, es };
export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English', htmlLang: 'en' },
  { code: 'pt', label: 'Português', htmlLang: 'pt-BR' },
  { code: 'es', label: 'Español', htmlLang: 'es' },
];
const SUPPORTED_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);
const STORAGE_KEY = 'dojo-mojo-lang';
const DEFAULT_LANG = 'en';

const I18nContext = createContext(null);

function detectLanguage() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_CODES.includes(stored)) return stored;
  } catch (_) {
    // localStorage may be unavailable (private mode, SSR)
  }
  const browser = (typeof navigator !== 'undefined' && navigator.language) || DEFAULT_LANG;
  const prefix = browser.toLowerCase().split('-')[0];
  return SUPPORTED_CODES.includes(prefix) ? prefix : DEFAULT_LANG;
}

function resolve(obj, path) {
  const parts = path.split('.');
  let current = obj;
  for (const p of parts) {
    if (current && typeof current === 'object' && p in current) {
      current = current[p];
    } else {
      return null;
    }
  }
  return typeof current === 'string' ? current : null;
}

function interpolate(str, params) {
  if (!params) return str;
  return str.replace(/\{\{(\w+)\}\}/g, (_, key) => (key in params ? String(params[key]) : `{{${key}}}`));
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectLanguage);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      const entry = SUPPORTED_LANGUAGES.find((l) => l.code === lang);
      document.documentElement.lang = entry ? entry.htmlLang : lang;
    }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (!SUPPORTED_CODES.includes(next)) return;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (_) {
      // ignore storage failures
    }
    setLangState(next);
  }, []);

  const t = useCallback(
    (key, params) => {
      const primary = resolve(translations[lang], key);
      if (primary !== null) return interpolate(primary, params);
      const fallback = resolve(translations.en, key);
      return fallback !== null ? interpolate(fallback, params) : key;
    },
    [lang]
  );

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}

export function getDateLocale(lang) {
  if (lang === 'pt') return 'pt-BR';
  if (lang === 'es') return 'es-ES';
  return 'en-US';
}

export function formatDateWithMonthName(date, lang) {
  return new Date(date).toLocaleDateString(getDateLocale(lang), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
